import { 
  flexRender, 
  useReactTable, 
  type ColumnDef, 
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  FilterFn,
  SortingState,
} from '@tanstack/react-table';
import React, { ReactNode, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { matchSorter } from 'match-sorter';
import { normalizeText } from '../lib/utils';
import { cn } from '../lib/utils';
import { useTablePagination } from '../hooks/useTablePagination';

interface ExpandableDataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  initialSorting?: SortingState;
  pageSize?: number;
  pageSizeOptions?: number[];
  totalItems?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  onRowClick?: (row: TData) => void;
  searchPlaceholder?: string;
  tableId?: string;
  renderExpanded?: (row: TData) => ReactNode;
  expandedRowId?: string;
  getRowId?: (row: TData) => string;
  preservePageOnDataChange?: boolean;
}

const fuzzyFilter: FilterFn<any> = (row, _columnId, value, _addMeta) => {
  const itemValues = row.getAllCells().map((cell: any) => {
    const cellValue = cell.getValue();
    return cellValue !== null && cellValue !== undefined ? String(cellValue) : '';
  });

  const item = itemValues.join(' ');
  
  const normalizedItem = normalizeText(item);
  const normalizedValue = normalizeText(value);
  
  const matches = matchSorter([normalizedItem], normalizedValue, { threshold: matchSorter.rankings.CONTAINS });
  
  return matches.length > 0;
};

export function ExpandableDataTable<TData>({ 
  data, 
  columns, 
  initialSorting = [],
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50, 100, 200],
  totalItems,
  onLoadMore,
  isLoadingMore = false,
  onRowClick,
  searchPlaceholder = "Search...",
  tableId = 'default',
  renderExpanded,
  expandedRowId,
  getRowId = (row: any) => row.id,
  preservePageOnDataChange = false
}: ExpandableDataTableProps<TData>) {
  const {
    currentPageSize,
    globalFilter,
    setGlobalFilter,
    paginationConfig
  } = useTablePagination({
    data,
    initialSorting,
    pageSize,
    preservePageOnDataChange,
    tableId
  });
  
  // Table configuration with explicit autoResetPageIndex: false
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuzzyFilter,
    manualPagination: Boolean(onLoadMore),
    pageCount: totalItems 
      ? Math.ceil(totalItems / currentPageSize)
      : undefined,
    getRowId: (row) => getRowId(row),
    ...paginationConfig
  });

  useEffect(() => {
    if (!onLoadMore) {
      if (table.getPageCount() <= table.getState().pagination.pageIndex) {
        table.setPageIndex(0);
      }
    }
  }, [table.getFilteredRowModel().rows.length, currentPageSize, onLoadMore]);
  
  return (
    <Card>
      <div className="p-4 border-b border-brand-border dark:border-brand-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 text-muted-foreground dark:text-muted-foreground -translate-y-1/2" />
          <Input
            placeholder={searchPlaceholder}
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`h-12 px-4 text-left align-middle text-sm font-medium text-muted-foreground dark:text-muted-foreground [&:has([role=checkbox])]:pr-0 ${
                      header.column.getCanSort() 
                        ? 'hover:bg-brand-dark dark:hover:bg-brand-dark/50 cursor-pointer' 
                        : ''
                    } ${
                      header.id === 'actions' ? 'w-[100px]' : header.id === 'status' ? 'w-[10px]' : ''
                    } transition-colors`}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {header.isPlaceholder ? null : (
                      header.id === 'actions' ? (
                        <div className="text-right">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            header.column.getIsSorted() === 'asc' ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            )
                          )}
                        </div>
                      )
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const isExpanded = expandedRowId === getRowId(row.original);
              
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={cn(
                      "border-t border-brand-border dark:border-brand-border hover:bg-brand-dark dark:hover:bg-brand-dark/50 transition-colors cursor-pointer",
                      isExpanded && "bg-brand-dark dark:bg-brand-dark/70"
                    )}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`p-4 align-middle text-sm [&:has([role=checkbox])]:pr-0 ${
                          cell.column.id === 'actions' ? 'text-right' : ''
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && renderExpanded && (
                    <tr className="border-none">
                      <td colSpan={columns.length} className="p-0 border-0">
                        <div className="animate-in fade-in-0 slide-in-from-top-5 zoom-in-95 duration-300">
                          {renderExpanded(row.original)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-brand-border dark:border-brand-border">
        <div className="flex items-center gap-4 text-sm text-muted-foreground dark:text-muted-foreground">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </span>
          <div className="flex items-center gap-2">
            <span>Show</span>
            <Select
              value={currentPageSize.toString()}
              onValueChange={(value) => {
                const newSize = Number(value);
                table.setPageSize(newSize);
              }}
            >
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>per page</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {onLoadMore && data.length < (totalItems || 0) && (
        <div className="flex items-center justify-center p-4 border-t border-brand-border dark:border-brand-border">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="w-full sm:w-auto"
          >
            {isLoadingMore ? (
              <span className="flex items-center gap-2">
                <div className="h-4 w-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
                Loading...
              </span>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}